// src/pages/HousingNavigatorChatPage.js — standalone demo page for the
// Housing Navigator MA help-center assistant. The chat itself is the
// production widget, framed directly from the chat backend.
//
// Branded to the HNMI Brand Style Guide 2023: Blue #0B66AD, Dark Blue #1F3466,
// Ice Blue #F2FAFF, Gray 20 #54595D, Gray 70 #C8CCD1; Roboto for body and
// headers, Faustina for titles; the logo in one colour on a solid background.
import React, { useEffect, useState } from 'react';
import HousingNavigatorDashboard from '../components/HousingNavigatorDashboard';

const WIDGET_ORIGIN = 'https://housing-external.onrender.com';
const WIDGET_SRC = `${WIDGET_ORIGIN}/widget-frame?org=housingnavigator&fresh=1`;

const HN = {
  blue: '#0B66AD',
  darkBlue: '#1F3466',
  iceBlue: '#F2FAFF',
  blueTint: '#E8F0F7',
  gray20: '#54595D',
  gray70: '#C8CCD1',
  sans: "'Roboto', -apple-system, 'Segoe UI', Arial, sans-serif",
  serif: "'Faustina', Georgia, 'Times New Roman', serif",
};

const HousingNavigatorChatPage = () => {
  // Page title (WCAG 2.4.2); the site-wide default names HousingAI, not this demo.
  // The chat's X ends the conversation and asks its host to close the chat
  // ('hw-close'). On a site the pop-up closes; here the chat is always open,
  // so reload it instead and the visitor gets a fresh conversation.
  const [frameKey, setFrameKey] = useState(0);
  // Demo control: show the widget in sleep mode (what visitors see once the
  // month's 500 conversations on housingnavigatorma.org are used up).
  const [sleepPreview, setSleepPreview] = useState(false);
  useEffect(() => {
    const onMessage = e => { if (e.origin === WIDGET_ORIGIN && e.data === 'hw-close') setFrameKey(k => k + 1); };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
  useEffect(() => {
    const prev = document.title;
    document.title = 'AI Search Mode – Housing Navigator MA Help Center Assistant demo';
    return () => { document.title = prev; };
  }, []);
  return (
  <div
    className="min-h-screen flex flex-col"
    style={{ background: HN.iceBlue, fontFamily: HN.sans, color: '#000' }}
  >
    <style>{`@import url('https://fonts.googleapis.com/css2?family=Faustina:wght@500;600;700&family=Roboto:ital,wght@0,400;0,500;0,700;1,400&display=swap');`}</style>

    <header className="w-full bg-white border-b" style={{ borderColor: HN.gray70 }}>
      <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <a href="https://housingnavigatorma.org/" target="_blank" rel="noopener noreferrer" aria-label="Housing Navigator Massachusetts">
          <img src="/housingnavigator-logo-full.svg" alt="Housing Navigator Massachusetts" style={{ height: 48, width: 'auto' }} />
        </a>
        <div className="text-right">
          <div className="text-sm font-medium" style={{ color: HN.darkBlue }}>AI Search Mode · Help Center Assistant</div>
          <div className="text-xs" style={{ color: HN.gray20 }}>Demo by HousingAI</div>
        </div>
      </div>
    </header>

    <main className="flex-1 flex flex-col items-center px-4 py-8">
      <h1 className="text-center mb-3" style={{ fontFamily: HN.serif, fontWeight: 600, fontSize: 'clamp(1.75rem, 4vw, 2.25rem)', lineHeight: 1.2, color: HN.darkBlue }}>
        AI Search Mode
      </h1>
      <p className="text-center w-full max-w-5xl mb-6" style={{ color: HN.darkBlue, fontSize: 16, lineHeight: 1.55 }}>
        It answers anything covered on Housing Navigator&apos;s Help Center and Resources pages, such as how the
        search engine works, eligibility and rent basics, lotteries and waitlists, and resources for
        service providers and property owners. It can also search Housing Navigator&apos;s live listings:
        ask for what you need in plain words, like <em>&ldquo;2 bedroom in Boston, wheelchair
        accessible&rdquo;</em> or <em>&ldquo;studios available now in Worcester under $1,500&rdquo;</em>,
        and it shows the matching properties with photos, availability, rent, and a link to each listing.
      </p>
      <div className="flex items-center gap-3 mb-3 text-sm" style={{ color: HN.gray20 }}>
        <span id="hn-sleep-label">Preview the monthly pause</span>
        <button
          type="button"
          role="switch"
          aria-checked={sleepPreview}
          aria-labelledby="hn-sleep-label"
          onClick={() => setSleepPreview(v => !v)}
          className="rounded-full px-3 py-1 text-sm font-semibold"
          style={{ background: sleepPreview ? HN.darkBlue : '#fff', color: sleepPreview ? '#fff' : HN.darkBlue, border: `1px solid ${HN.darkBlue}` }}
        >
          {sleepPreview ? 'On' : 'Off'}
        </button>
      </div>
      <div
        className="w-full rounded-2xl overflow-hidden bg-white"
        style={{ maxWidth: 440, height: 'min(80vh, 760px)', border: `1px solid ${HN.gray70}`, boxShadow: '0 12px 32px rgba(31, 52, 102, 0.14)' }}
      >
        <iframe
          key={`${frameKey}-${sleepPreview ? 'asleep' : 'awake'}`}
          title="AI Search Mode, Housing Navigator MA assistant"
          src={sleepPreview ? `${WIDGET_SRC}&sleep=preview` : WIDGET_SRC}
          allow="microphone"
          style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
        />
      </div>
      <p className="text-sm mt-4 text-center max-w-xl" style={{ color: HN.gray20 }}>
        Help Center answers come only from Housing Navigator&apos;s Resources page. Listings come straight from
        Housing Navigator&apos;s search engine and change daily.
      </p>
      <HousingNavigatorDashboard />
    </main>
  </div>
  );
};

export default HousingNavigatorChatPage;
